import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ChatApiService } from '../../services/features/chat/chat-api.service';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ToasterService } from '../../shared/toaster/toaster.service';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';

interface ChatMessage {
  id: string;
  from: 'me' | 'them';
  text: string;
  timestamp: Date;
  read: boolean;
  status?: 'sent' | 'delivered' | 'read';
  replyTo?: string;
  isDeleted?: boolean;
  senderId?: string;
  receiverId?: string;
}

interface ContextMenu {
  visible: boolean;
  x: number;
  y: number;
  message: ChatMessage | null;
  index: number;
}

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, SpinnerComponent]
})
export class ChatComponent implements OnInit, OnDestroy {
  private messageSub?: Subscription;
  newMessage = '';
  messages: ChatMessage[] = [];
  currentUserId: string = '';
  contactId: string = '';
  contactName: string = '';
  contactUsername: string = '';
  isChatActive = false;
  ringing = false;
  ringtoneAudio = new Audio('assets/sounds/ringing.mp3');

  // Pagination state
  page = 0;
  limit = 30;
  loadingMore = false;
  allLoaded = false;

  contextMenu: ContextMenu = { visible: false, x: 0, y: 0, message: null, index: -1 };

  incomingCall: { fromUserId: string, callType: 'audio' | 'video', fromName?: string } | null = null;
  callActive = false;
  callType: 'audio' | 'video' = 'audio';

  localStream?: MediaStream;
  remoteStream?: MediaStream;
  peerConnection?: RTCPeerConnection;
  pendingCandidates: RTCIceCandidate[] = [];
  remoteDescriptionSet = false;
  spinner = false;
  spinMessage = '';
  vibrationInterval: any = null;


  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  @ViewChild('localVideo') localVideoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteVideo') remoteVideoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteAudio') remoteAudioRef?: ElementRef<HTMLAudioElement>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private chatService: ChatApiService,
    private toasterService: ToasterService
  ) { }

  ngOnInit() {
     this.ringtoneAudio.loop = true;
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    this.currentUserId = user._id;
    this.chatService.registerUser(user._id);

    this.chatService.socket.on('chatError', (data: any) => {
      if (data && data.error) {
        this.toasterService.showToast(data.error, 'error');
      }
    });

    this.route.params.subscribe(params => {
      this.contactId = params['userId'];
      this.fetchContactInfo();
      this.page = 0;
      this.allLoaded = false;
      this.fetchChatHistory(true);
      this.isChatActive = true;

      // Emit joinChat when entering the chat
      this.chatService.socket.emit('joinChat', {
        userId: this.currentUserId,
        chatWithUserId: this.contactId
      });
    });

    this.messageSub = this.chatService.onMessage().subscribe((msg) => {
      // Only add the message if it's for the current chat
      const isCurrentChat =
        (msg.senderId === this.currentUserId && msg.receiverId === this.contactId) ||
        (msg.senderId === this.contactId && msg.receiverId === this.currentUserId);

      if (isCurrentChat && !this.messages.some(m => m.id === msg._id)) {
        this.messages.push({
          id: msg._id,
          from: msg.senderId === this.currentUserId ? 'me' : 'them',
          text: msg.content,
          timestamp: this.parseValidDate(msg.timestamp),
          read: false,
          status: 'delivered',
          senderId: msg.senderId,
          receiverId: msg.receiverId
        });
        setTimeout(() => this.scrollToBottom(), 100);
      }
      if (this.isChatActive && msg.senderId === this.contactId) {
        this.markMessagesRead();
      }
    });

    // --- Call signaling events ---
    this.chatService.onIncomingCall((data) => {
      this.incomingCall = data;
      this.callType = data.callType;
      this.ringing = false;
       this.playRingtone();
       this.startVibration();
    });

    this.chatService.onCallBusy(() => {
      this.stopRingtone();
      this.ringing = false;
      this.toasterService.showToast('User is busy on another call.', 'info');
      this.incomingCall = null;
    });

    this.chatService.onCallRejected((data) => {
      this.stopRingtone();
      this.ringing = false;
      if (data?.reason === 'not-liked') {
        this.toasterService.showToast('You can only call users who liked you back.', 'error');
      } else if (data?.reason === 'not-in-chat') {
        this.toasterService.showToast('You can only call when both users are in the same chat.', 'error');
      } else {
        this.toasterService.showToast('User Busy.', 'info');
      }
      this.callActive = false;
      this.closeWebRTC();
      this.incomingCall = null;
    });

    this.chatService.onCallEnded(() => {
       this.stopRingtone(); 
      this.ringing = false;
      this.incomingCall = null;
      this.closeWebRTC();
      this.callActive = false;
    });

    // --- WebRTC signaling events ---
    this.chatService.onWebrtcOffer(async (data) => {
      await this.startWebRTC(false, this.callType);
      await this.peerConnection!.setRemoteDescription(new RTCSessionDescription(data.offer));
      this.remoteDescriptionSet = true;
      while (this.pendingCandidates.length) {
        await this.peerConnection!.addIceCandidate(this.pendingCandidates.shift()!);
      }
      const answer = await this.peerConnection!.createAnswer();
      await this.peerConnection!.setLocalDescription(answer);
      this.chatService.sendWebrtcAnswer(data.fromUserId, this.currentUserId, answer);
      this.callActive = true;
    });

    this.chatService.onWebrtcAnswer(async (data) => {
      await this.peerConnection!.setRemoteDescription(new RTCSessionDescription(data.answer));
      this.remoteDescriptionSet = true;
      while (this.pendingCandidates.length) {
        await this.peerConnection!.addIceCandidate(this.pendingCandidates.shift()!);
      }
    });

    this.chatService.onWebrtcIceCandidate(async (data) => {
      if (data.candidate) {
        if (this.peerConnection && this.remoteDescriptionSet) {
          await this.peerConnection.addIceCandidate(new RTCIceCandidate(data.candidate));
        } else {
          this.pendingCandidates.push(new RTCIceCandidate(data.candidate));
        }
      }
    });

    this.chatService.socket.on('callAccepted', () => {
       this.stopRingtone(); 
      this.callActive = true;
      this.ringing = false;
      this.startWebRTC(true, this.callType);
    });

    this.route.queryParams.subscribe(params => {
      if (params['acceptCall']) {
        this.callType = params['callType'] || 'audio';
        this.acceptCall();
      }
    });
  }


  playRingtone() {
  this.ringtoneAudio.currentTime = 0;
  this.ringtoneAudio.play().catch(() => {});
}

stopRingtone() {
  this.ringtoneAudio.pause();
  this.ringtoneAudio.currentTime = 0;
}


startVibration() {
  if (!('vibrate' in navigator)) return;

  // Prevent multiple intervals
  if (this.vibrationInterval) return;

  // Vibrate pattern repeatedly
  this.vibrationInterval = setInterval(() => {
    navigator.vibrate([1000, 500]);
  }, 1500);
}

stopVibration() {
  if (!('vibrate' in navigator)) return;

  if (this.vibrationInterval) {
    clearInterval(this.vibrationInterval);
    this.vibrationInterval = null;
  }

  navigator.vibrate(0); // force stop
}




  onScrollUp(event: any) {
    const container = event.target as HTMLElement;
    if (container.scrollTop === 0 && !this.loadingMore && !this.allLoaded) {
      this.page++;
      this.fetchChatHistory(false);
    }
  }

  private scrollToBottom(): void {
    try {
      this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
    } catch (err) { }
  }

  ngOnDestroy() {
    this.stopRingtone();
    this.stopVibration();
    if (this.messageSub) {
      this.messageSub.unsubscribe();
    }
    this.isChatActive = false;
    this.closeWebRTC();
    this.chatService.socket.emit('leaveChat', this.currentUserId);
  }

  async startWebRTC(isCaller: boolean, callType: 'audio' | 'video') {
    const servers = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };
    this.peerConnection = new RTCPeerConnection(servers);

    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: callType === 'video'
    });

    this.localStream.getTracks().forEach(track => {
      this.peerConnection!.addTrack(track, this.localStream!);
    });

    // Attach local stream to local video element
    if (callType === 'video' && this.localVideoRef) {
      this.localVideoRef.nativeElement.srcObject = this.localStream;
    }

    this.peerConnection.ontrack = (event) => {
      this.remoteStream = event.streams[0];
      if (this.callType === 'video' && this.remoteVideoRef) {
        this.remoteVideoRef.nativeElement.srcObject = this.remoteStream;
      } else if (this.callType === 'audio' && this.remoteAudioRef) {
        this.remoteAudioRef.nativeElement.srcObject = this.remoteStream;
      }
    };

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.chatService.sendWebrtcIceCandidate(this.contactId, this.currentUserId, event.candidate);
      }
    };

    if (isCaller) {
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);
      this.chatService.sendWebrtcOffer(this.contactId, this.currentUserId, offer);
    }
  }

  closeWebRTC() {
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = undefined;
    }
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = undefined;
    }
    this.remoteStream = undefined;
    // Clear video/audio elements
    if (this.remoteVideoRef && this.remoteVideoRef.nativeElement) {
      this.remoteVideoRef.nativeElement.srcObject = null;
    }
    if (this.localVideoRef && this.localVideoRef.nativeElement) {
      this.localVideoRef.nativeElement.srcObject = null;
    }
    if (this.remoteAudioRef && this.remoteAudioRef.nativeElement) {
      this.remoteAudioRef.nativeElement.srcObject = null;
    }

    this.remoteDescriptionSet = false;
    this.pendingCandidates = [];
  }

  private parseValidDate(value: any): Date {
    const date = new Date(value);
    return isNaN(date.getTime()) ? new Date() : date;
  }

  fetchChatHistory(initial = false) {
    const token = sessionStorage.getItem('token');
    if (!token || !this.contactId) return;

    this.spinner = true;
    this.spinMessage = initial ? 'Loading chat...' : 'Loading more messages...';

    const skip = this.page * this.limit;
    this.loadingMore = true;
    this.chatService.getChatHistory(this.contactId, token, this.limit, skip).subscribe((res: any) => {
      const newMessages = (res.messages || []).map((msg: any) => ({
        id: msg._id,
        from: msg.sender === this.currentUserId ? 'me' : 'them',
        text: msg.content,
        timestamp: this.parseValidDate(msg.timestamp || msg.createdAt),
        read: msg.read,
        status: msg.read ? 'read' : 'delivered',
        replyTo: msg.replyTo,
        isDeleted: msg.isDeleted,
        senderId: msg.sender,
        receiverId: msg.receiver
      }));
      if (initial) {
        this.messages = newMessages;
        setTimeout(() => this.scrollToBottom(), 100);
      } else {
        const prevHeight = this.messagesContainer.nativeElement.scrollHeight;
        this.messages = [...newMessages, ...this.messages];
        setTimeout(() => {
          this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight - prevHeight;
        }, 100);
      }
      this.loadingMore = false;
      if (newMessages.length < this.limit) {
        this.allLoaded = true;
      }
      this.markMessagesRead();
      this.spinner = false;
      this.spinMessage = '';
    });
  }

  markMessagesRead() {
    const token = sessionStorage.getItem('token');
    if (!token || !this.contactId) return;
    this.chatService.markMessagesRead(this.contactId, token).subscribe();
  }

  fetchContactInfo() {
    const token = sessionStorage.getItem('token');
    if (!token || !this.contactId) return;
      this.spinner = true;
  this.spinMessage = 'Fetching contact info...';
    this.chatService.getUserById(this.contactId, token).subscribe((res: any) => {
      this.contactName = res.user?.originalName || res.user?.username || 'Stranger';
      this.contactUsername = res.user?.username || 'unknown';
      this.spinner = false;
      this.spinMessage = '';
    });
  }

  sendMessage() {
    if (this.newMessage.trim()) {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      if (!user._id || !this.contactId) {
        this.toasterService.showToast('User info missing!', 'error');
        return;
      }
      this.chatService.sendChatMessage(this.currentUserId, this.contactId, this.newMessage);
      // this.toasterService.showToast('Message sent!', 'success');
      this.newMessage = '';
    } else {
      this.toasterService.showToast('Please enter a message before sending.', 'info');
    }
  }


  showContextMenu(event: MouseEvent, index: number) {
    event.preventDefault();
    this.contextMenu = {
      visible: true,
      x: event.clientX,
      y: event.clientY,
      message: this.messages[index],
      index: index
    };
  }
  hideContextMenu() {
    this.contextMenu.visible = false;
  }

  startCall(callType: 'audio' | 'video') {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (!user._id || !this.contactId) {
      this.toasterService.showToast('User info missing!', 'error');
      return;
    }
    this.callType = callType;
    this.ringing = true;
    this.playRingtone();
    this.chatService.callUser(user._id, this.contactId, callType);
  }

  acceptCall() {
     this.stopRingtone(); 
       this.stopVibration();
    this.chatService.acceptCall(this.incomingCall ? this.incomingCall.fromUserId : this.contactId, this.currentUserId);
    this.incomingCall = null;
    this.callActive = true;
    this.startWebRTC(false, this.callType);
  }

  rejectCall() {
     this.stopRingtone(); 
      this.stopVibration();
    this.chatService.rejectCall(this.incomingCall!.fromUserId, this.currentUserId);
    this.incomingCall = null;
  }

  endCall() {
     this.stopRingtone(); 
     this.stopVibration();
    this.chatService.socket.emit('endCall', { fromUserId: this.currentUserId, toUserId: this.contactId });
    this.callActive = false;
    this.ringing = false;
    this.incomingCall = null;
    this.closeWebRTC();
  }

  cancelCall() {
     this.stopRingtone(); 
     this.stopVibration();
    this.ringing = false;
    this.callActive = false;
    this.incomingCall = null;
    this.chatService.socket.emit('endCall', { fromUserId: this.currentUserId, toUserId: this.contactId });
    this.closeWebRTC();
  }

  getMessagePreview(messageId: string | undefined): string {
    if (!messageId) return 'Original message';
    const message = this.messages.find(m => m.id === messageId);
    if (!message) return 'Message not found';
    return message.text.length > 30 ? message.text.substring(0, 30) + '...' : message.text;
  }

  goBack() {
    this.isChatActive = false;
    this.router.navigate(['/home']);
  }
}